import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { AppLayout } from "../components/AppLayout";
import { StatusBadge } from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Skeleton } from "../components/ui/skeleton";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { formatDateTime } from "../lib/format";
import {
  ArrowRight, Truck, Lock, Zap, MousePointerClick, Calendar, Euro,
  Download, CheckCircle2, ShieldCheck, XCircle, PartyPopper, MapPin, Archive,
} from "lucide-react";

const PreviewRow = ({ label, value }) => (
  <div className="flex justify-between text-sm py-1.5">
    <span className="text-slate-500">{label}</span>
    <span className="font-medium text-slate-800">{value || "—"}</span>
  </div>
);

export default function PartnerDashboard() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/requests");
    setItems(data);
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [load]);

  const handleUnavailable = (id, err) => {
    const av = err.response?.headers?.["x-availability"];
    const detail = formatApiErrorDetail(err.response?.data?.detail);
    if (err.response?.status === 409 && (av === "assegnato" || av === "non_disponibile")) {
      setItems((prev) => prev?.filter((i) => i.id !== id) || prev);
      toast.error(av === "assegnato" ? "Servizio già assegnato" : "Servizio non più disponibile",
        { description: detail, duration: 7000 });
    } else {
      toast.error("Operazione non consentita", { description: detail });
    }
  };

  const accept = async (id) => {
    setBusy(id);
    try {
      await api.post(`/requests/${id}/accept`);
      toast.success("Corsa aggiudicata!", { description: "Il foglio di servizio completo è stato sbloccato." });
      await load();
    } catch (err) {
      handleUnavailable(id, err);
      await load();
    } finally { setBusy(null); }
  };

  const candidate = async (id) => {
    setBusy(id);
    try {
      await api.post(`/requests/${id}/candidate`);
      toast.success("Candidatura inviata", { description: "La Centrale Operativa valuterà la tua disponibilità." });
      await load();
    } catch (err) {
      handleUnavailable(id, err);
      await load();
    } finally { setBusy(null); }
  };

  const reject = async (id) => {
    setBusy(id);
    try {
      await api.post(`/requests/${id}/reject`);
      toast("Corsa rifiutata", { description: "Non comparirà più tra le corse disponibili." });
      await load();
    } catch (err) {
      handleUnavailable(id, err);
      await load();
    } finally { setBusy(null); }
  };

  const downloadPdf = async (item) => {
    try {
      const res = await api.get(`/requests/${item.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `Foglio_Servizio_${item.code?.replace("/", "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("PDF non disponibile"); }
  };

  const assignedToMe = items?.filter((i) => i.unlocked && !["completata", "da_fatturare", "annullata"].includes(i.status)) || [];
  const archivedCount = items?.filter((i) => i.unlocked && ["completata", "da_fatturare", "annullata"].includes(i.status)).length || 0;
  const available = items?.filter((i) => !i.unlocked) || [];

  return (
    <AppLayout>
      <div className="max-w-5xl mx-auto nx-fade-in">
        <h1 className="text-3xl font-bold font-heading text-slate-900 tracking-tight mb-1">Corse Disponibili</h1>
        <p className="text-sm text-slate-500 mb-6 flex items-center gap-1.5">
          <ShieldCheck className="h-4 w-4 text-emerald-600" /> Dati sensibili e indirizzi esatti visibili solo dopo l'assegnazione (GDPR)
        </p>

        {archivedCount > 0 && (
          <button type="button" data-testid="archive-link" onClick={() => navigate("/partner/archivio")}
            className="mb-6 inline-flex items-center gap-2 text-xs font-medium text-slate-600 hover:text-primary bg-slate-100 hover:bg-slate-200 rounded-full px-3 py-1.5">
            <Archive className="h-3.5 w-3.5" /> {archivedCount} servizi completati/annullati in archivio <ArrowRight className="h-3 w-3" />
          </button>
        )}

        {items === null ? (
          <div className="grid md:grid-cols-2 gap-4">
            {[...Array(4)].map((_, i) => <Skeleton key={`skeleton-${i}`} className="h-56 w-full" />)}
          </div>
        ) : (
          <>
            {assignedToMe.length > 0 && (
              <div className="mb-8">
                <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wider mb-3">I miei servizi assegnati</h2>
                <div className="grid md:grid-cols-2 gap-4">
                  {assignedToMe.map((i) => (
                    <Card key={i.id} data-testid={`assigned-card-${i.id}`} className="p-5 border-[#C6AD76] bg-[#FAF7F0]">
                      <div className="flex items-center justify-between mb-3">
                        <span className="font-mono text-xs font-bold text-primary">{i.code}</span>
                        <StatusBadge status={i.status} />
                      </div>
                      <p data-testid={`assigned-datetime-${i.id}`} className="text-sm font-semibold text-slate-800 flex items-center gap-1.5 mb-2 bg-white/70 border border-[#C6AD76] rounded-md px-2.5 py-1.5 w-fit">
                        <Calendar className="h-4 w-4 text-primary" />
                        {formatDateTime(i.operative?.data_ora_evento) || "Data da definire"}
                        {i.request_type === "evento" && i.evento?.data_ora_fine && (
                          <span className="text-xs font-normal text-slate-500">→ fine {formatDateTime(i.evento.data_ora_fine)}</span>
                        )}
                      </p>
                      {i.request_type === "evento" ? (
                        <>
                          <p className="text-base font-semibold text-slate-800 flex items-center gap-1.5 mb-1">
                            <PartyPopper className="h-4 w-4 text-fuchsia-600" /> {i.evento?.denominazione || "Evento"}
                          </p>
                          <p className="text-xs text-slate-500 mb-1 flex items-center gap-1"><MapPin className="h-3 w-3" /> {i.evento?.luogo}</p>
                          <p className="text-xs text-slate-500 mb-3">Referente: <span className="font-medium text-slate-700">{i.evento?.referente || "—"}</span> · Tel: {i.evento?.telefono_referente || "—"}</p>
                        </>
                      ) : (
                        <>
                          <div className="mb-2 space-y-1">
                            <p className="text-sm font-semibold text-slate-800 flex items-start gap-1.5"><span className="text-[10px] uppercase tracking-wide text-slate-400 w-16 shrink-0 pt-0.5">Partenza</span> {i.partenza?.citta}</p>
                            <p className="text-sm font-semibold text-slate-800 flex items-start gap-1.5"><span className="text-[10px] uppercase tracking-wide text-slate-400 w-16 shrink-0 pt-0.5">Arrivo</span> {i.arrivo?.citta}</p>
                          </div>
                          <p className="text-xs text-slate-500 mb-1">Paziente: <span className="font-medium text-slate-700">{i.patient?.nominativo}</span> · Tel: {i.patient?.telefono}</p>
                          <p className="text-xs text-slate-500 mb-1">Partenza: {i.partenza?.indirizzo}, {i.partenza?.citta}</p>
                          <p className="text-xs text-slate-500 mb-3">Arrivo: {i.arrivo?.indirizzo}, {i.arrivo?.citta}</p>
                        </>
                      )}
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" className="gap-1.5 flex-1" onClick={() => downloadPdf(i)} data-testid={`partner-pdf-${i.id}`}>
                          <Download className="h-4 w-4" /> Foglio Servizio
                        </Button>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wider mb-3">Anteprima corse pubblicate</h2>
            {available.length === 0 ? (
              <Card className="py-16 text-center border-slate-200">
                <Truck className="h-10 w-10 mx-auto text-slate-300 mb-3" />
                <p className="text-slate-500 text-sm">Nessuna corsa disponibile al momento.</p>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {available.map((i) => {
                  return (
                    <Card key={i.id} data-testid={`preview-card-${i.id}`} className="p-5 border-slate-200 hover:border-primary/40 transition-colors">
                      <div className="flex items-center justify-between mb-3">
                        <span className="font-mono text-xs font-bold text-primary">{i.code}</span>
                        <Badge variant="outline" className="gap-1 text-[10px]">
                          {i.engagement_mode === "fcfs"
                            ? <><Zap className="h-3 w-3" /> Accetta subito</>
                            : <><MousePointerClick className="h-3 w-3" /> Candidatura</>}
                        </Badge>
                      </div>

                      {i.request_type === "evento" ? (
                        <>
                          <p className="text-base font-semibold text-slate-800 flex items-center gap-1.5 mb-3">
                            <PartyPopper className="h-4 w-4 text-fuchsia-600" /> {i.evento?.denominazione || "Evento"}
                          </p>
                          <div className="rounded-md bg-slate-50 border border-slate-100 px-3 py-2 mb-3">
                            <PreviewRow label="Tipo" value={i.evento?.tipo} />
                            <PreviewRow label="Luogo" value={i.evento?.luogo} />
                            <PreviewRow label="Inizio" value={formatDateTime(i.evento?.data_ora_inizio)} />
                            <PreviewRow label="Fine" value={formatDateTime(i.evento?.data_ora_fine)} />
                            <PreviewRow label="Risorse" value={(i.evento?.risorse_list || []).map((r) => `${r.label} x${r.qty}`).join(", ")} />
                            <div className="flex justify-between text-sm py-1.5 border-t border-slate-200 mt-1">
                              <span className="text-slate-500 flex items-center gap-1"><Euro className="h-3.5 w-3.5" /> Importo</span>
                              <span className="font-bold text-emerald-700">€ {i.importo || "—"}</span>
                            </div>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="mb-3 space-y-1" data-testid={`preview-route-${i.id}`}>
                            <p className="text-base font-semibold text-slate-800 flex items-start gap-2"><span className="text-[10px] uppercase tracking-wide text-slate-400 w-16 shrink-0 pt-1.5">Partenza</span> {i.citta_partenza || "—"}</p>
                            <p className="text-base font-semibold text-slate-800 flex items-start gap-2"><span className="text-[10px] uppercase tracking-wide text-slate-400 w-16 shrink-0 pt-1.5">Arrivo</span> {i.citta_arrivo || "—"}</p>
                          </div>
                          <div className="rounded-md bg-slate-50 border border-slate-100 px-3 py-2 mb-3">
                            <PreviewRow label="Data / Ora" value={formatDateTime(i.data_ora_evento)} />
                            <PreviewRow label="Mezzo" value={i.tipologia_mezzo} />
                            <PreviewRow label="Mobilizzazione" value={i.mobilizzazione} />
                            <PreviewRow label="Tratta" value={i.tratta} />
                            <div className="flex justify-between text-sm py-1.5 border-t border-slate-200 mt-1">
                              <span className="text-slate-500 flex items-center gap-1"><Euro className="h-3.5 w-3.5" /> Importo trasporto</span>
                              <span className="font-bold text-emerald-700">€ {i.importo || "—"}</span>
                            </div>
                          </div>
                        </>
                      )}

                      <p className="text-[11px] text-slate-400 flex items-center gap-1 mb-3">
                        <Lock className="h-3 w-3" /> Indirizzi, nome e contatti sbloccati dopo l'accettazione
                      </p>

                      {i.engagement_mode === "fcfs" ? (
                        <Button data-testid={`accept-${i.id}`} className="w-full gap-2" disabled={busy === i.id}
                          onClick={() => accept(i.id)}>
                          <CheckCircle2 className="h-4 w-4" /> {busy === i.id ? "Attendere..." : "Accetta Corsa"}
                        </Button>
                      ) : (
                        <Button data-testid={`candidate-${i.id}`} variant="outline" className="w-full gap-2" disabled={busy === i.id}
                          onClick={() => candidate(i.id)}>
                          <MousePointerClick className="h-4 w-4" /> {busy === i.id ? "Attendere..." : "Mi Candido"}
                        </Button>
                      )}
                      <Button data-testid={`reject-${i.id}`} variant="ghost" className="w-full gap-2 mt-2 text-red-600 hover:bg-red-50 hover:text-red-700"
                        disabled={busy === i.id} onClick={() => reject(i.id)}>
                        <XCircle className="h-4 w-4" /> Rifiuta
                      </Button>
                    </Card>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </AppLayout>
  );
}
